import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { ErrorBoundary } from 'react-error-boundary';
import { Toast } from '@statsbomb/kitbag-components';
import uiStructure from '../../apollo/uiStructure';
import ContextFilters from '../ContextFilters/ContextFilters';
import Header from '../Header/Header';
import {
  ui_isDark,
  mf_Leagues,
  mf_Games,
  mf_Seasons,
  mf_Teams,
  pf_Drive,
  pf_TeamPlay,
  pf_TeamPlayEvent,
  mf_Players,
  mf_LeagueLevel,
  cf_GameIds,
  mf_TeamDetails,
  mf_PlayerDetails,
  mf_GameDetails,
  mf_AllSeasons,
} from '../../apollo';
import { StyledLayout, Main } from './Layout.styles';
import { GET_NAVIGATION } from '../Header/getNavigationData';
import { ERROR_CODES } from '../../utils/errorCodes/errorCodes';
import { replaceTokens } from '../../utils/helpers/general';
import {
  isFalsyParam,
  isGameOverviewRoute,
  isGameResolvable,
  isTeamResolvable,
} from './Layout.helper';
import { GET_GAMES } from '../Header/getGames';
import { pitchCompetitionLevel } from '../../utils/helpers/field.constants';
import SideNav from '../SideNavigation';
import ErrorPage from '../ErrorPage/ErrorPage';
import { useTrackPageLoad } from '../../utils/hooks/useTrackPageLoad';
import { findPageContext } from '../../utils/helpers/findPageContext';
import { buildConditionEntitiesFromUrl } from '../../utils/helpers/buildConditionEntitiesFromUrl';
import { shouldTrackPageView } from '../../utils/helpers/shouldTrackPageView';
import WhatsNew from '../WhatsNew/WhatsNew';
import { useStore } from '../../store/useStore';

const { getSnowplowTracker, HudlDataSchemas } = require('@hudl/analytics');

/* 
We don't have fully automated tests for this component, but we can test it manually.
Test Notes:
  - Change the league, this will invalidate the team and player. 
  - Check on any given page that the charts reflect this change. NCAA Division III is troublesome.
  - Change the season, it should maintain the team
  - Check initial load, open a new tab and paste in the URL (with an empty URL) 
    -- account defaults i.e. Baltimore should load
  - Check configured a URL, open a new tab and paste in the URL (http://localhost:3000/1446/1034/1001437) - the league/season/team should load
 - Make a change that will lead to a null in any of the given league/season/team
 - Where league is set make sure league level is set correctly.
    -- All non-NFL leagues are "NCAA" in terms of hash / pitch markings 
      ~ values from the API of "NCAA_DIVISION_II" are not supported/useful so ignore that
    -- live: go to a pitch vis (snap distro, animation for example). Change comp -> pitch doesn't change.
    -- here: same thing the pitch should redraw hashes appropriately.

  -Play Animation
    select different drives and plays
    copy the URL and paste into a new browser window
    hard refresh the current page
    Test Drives play changes in conjunction with:
      Snap Formations
      Play Events
    -Goto the homepage, hard refresh (to reset all internal vars) and navigate to Play Animation
*/
const Layout = () => {
  // Track page load performance and send Snowplow DurationEvent
  useTrackPageLoad();
  const navigate = useNavigate();
  const {
    seasons: seasonsParam,
    leagues: leaguesParam,
    teams: teamsParam,
    players: playersParam,
    games: gamesParam,
  } = useParams();
  const ui_isDarkRV = useReactiveVar(ui_isDark);
  const [pageInfo, setPageInfo] = useState();
  // Toast
  const setToastVisibility = useStore((s) => s.setToastVisibility);
  const showToast = useStore((s) => s.showToast);
  const toast = useStore((s) => s.toast);

  const location = useLocation();
  // trim leading slash
  let currentLocation = location.pathname.slice(1);
  // trim trailing slash if present
  currentLocation = currentLocation.endsWith('/')
    ? currentLocation.slice(0, -1)
    : currentLocation;

  // Game Overview is the only route that opts into T1 (pre-snap-only) games, so
  // both the navigation data and the games list are scoped to it: the team
  // selector's [X] count then matches the games dropdown on that page and stays
  // T3-only everywhere else.
  const includeT1Games = isGameOverviewRoute(location.pathname);

  // navigation query to populate: league / season / team / player data
  const {
    data: seasonLeagueTeamPlayersData,
    loading: isLoading,
    error: hasError,
  } = useQuery(GET_NAVIGATION, {
    variables: { includeT1Games },
  });
  if (hasError) {
    console.error(ERROR_CODES.GET_NAVIGATION_DATA, hasError);
  }
  // navigation data
  const { seasons, teams, players } =
    seasonLeagueTeamPlayersData?.navigationData || {};

  // games
  const {
    data: gamesData,
    loading: isLoadingGamesData,
    error: gamesError,
  } = useQuery(GET_GAMES, {
    variables: { includeT1Games },
  });
  if (gamesError) {
    console.error(ERROR_CODES.GET_GAMES, gamesError);
  }

  let snowplowTracker;

  useLayoutEffect(() => {
    uiStructure.pages.forEach((page) => {
      page.path.forEach((url) => {
        if (new RegExp(url, 'g').test(currentLocation)) {
          if (page !== pageInfo) {
            setPageInfo(page);
          }
        }
      });
    });

    // Snowplow tracker
    if (/true/i.test(process.env.SNOWPLOW_TRACKING_ON)) {
      try {
        snowplowTracker = getSnowplowTracker('hudlIQ');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error getting Snowplow tracker. Error: ', error);
      }

      if (snowplowTracker && shouldTrackPageView(location.pathname)) {
        const { page_type, page_sub_type } = findPageContext(location.pathname);

        const pageEntities = [];

        // Always send PageEntity
        pageEntities.push(
          HudlDataSchemas.PageEntity.V2_0_2.describe({
            page_url: window.location.href,
            page_type,
            page_sub_type,
            generic_page_url: location.pathname,
          }),
        );

        // Build Condition Entities if matching page
        const conditionEntities = buildConditionEntitiesFromUrl(
          location.pathname,
          uiStructure,
        );
        pageEntities.push(...conditionEntities);

        // Fire page view event
        snowplowTracker.trackPageViewEvent(location.pathname, pageEntities);

        // Fire page pings
        /**
         * Tracks page pings to measure time spent on a page
         * @param minimumVisitLength The minimum visit length before the first ping is sent (in seconds)
         * @param heartbeatDelay The interval at which pings are sent (in seconds)
         */
        snowplowTracker.trackPagePings(1, 10);
      }
    }
  }, [location]);

  const useFilter = pageInfo?.contextFilters;

  // give precedence to url params so they persist on refresh and url sharing
  useEffect(() => {
    if (leaguesParam && !isFalsyParam(leaguesParam)) {
      const leagueId = parseInt(leaguesParam, 10);

      mf_Leagues(leagueId);
      mf_LeagueLevel(pitchCompetitionLevel(leagueId));
    }
    if (seasonsParam && !isFalsyParam(seasonsParam)) {
      mf_Seasons(parseInt(seasonsParam, 10));
    }
    if (teamsParam && !isFalsyParam(teamsParam)) {
      mf_Teams(parseInt(teamsParam, 10));
    }
    if (playersParam && !isFalsyParam(playersParam)) {
      mf_Players(parseInt(playersParam, 10));
    }
    if (gamesParam && !isFalsyParam(gamesParam)) {
      mf_Games(parseInt(gamesParam, 10));
    }
  }, [leaguesParam, seasonsParam, teamsParam, playersParam, gamesParam]);

  const handleChange = (selectedOption, filter) => {
    const { value } = selectedOption;

    if (filter === 'leagues') {
      mf_Leagues(value);
      // assume change in league and team will invalidate team, player, and games
      mf_Teams(null);
      mf_Players(null);
      mf_Games(null);
      mf_GameDetails(null);
      mf_LeagueLevel(pitchCompetitionLevel(value));
      cf_GameIds([]);
    } else if (filter === 'seasons') {
      pf_TeamPlay(null);
      pf_Drive(null);
      mf_Seasons(value);
      mf_Games(null);
      mf_GameDetails(null);
      // nice UX to keep the team selected
      // ...but the first player may change
      mf_Players(null);
      cf_GameIds([]);
    } else if (filter === 'teams') {
      pf_TeamPlay(null);
      pf_Drive(null);
      mf_Teams(value);
      mf_Players(null);
      mf_TeamDetails(teams?.find((t) => t?.id === value));
      mf_Games(null);
      mf_GameDetails(null);
      cf_GameIds([]);
    } else if (filter === 'players') {
      mf_Players(value);
      mf_PlayerDetails(players?.find((p) => p?.id === value));
    } else if (filter === 'games') {
      pf_TeamPlay(null);
      pf_Drive(null);
      pf_TeamPlayEvent(null);
      mf_Games(value);
      mf_GameDetails(
        gamesData?.games?.edges.find((game) => game.node.id === value)?.node,
      );
    }

    const instantRV = {
      leagues: mf_Leagues(),
      players: mf_Players(),
      seasons: mf_Seasons(),
      teams: mf_Teams(),
      games: mf_Games(),
      drives: pf_Drive(),
      plays: pf_TeamPlay(),
    };

    if (pageInfo) {
      navigate(
        `${replaceTokens(window.location.pathname, pageInfo, instantRV)}${
          window.location.search
        }`,
        { replace: false },
      );
    }
  };

  if (seasonLeagueTeamPlayersData) {
    // Recover when the URL names a team the loaded list doesn't contain, as well
    // as when the param is missing. GET_NAVIGATION is route-scoped - only Game
    // Overview asks for T1 games - so a team whose only games are T1 is absent
    // from the list on every other route. Without this, the else branch leaves
    // mf_TeamDetails undefined (blank team name in page headers and chart
    // exports) while mf_Teams and the URL still hold the missing id, and the
    // games recovery below then has an empty list to pick a default from.
    // Guarded on teamsParam being present so routes with no teams segment (e.g.
    // league pages) keep resolving their details from the reactive var instead
    // of triggering a spurious redirect.
    if (
      teamsParam !== undefined &&
      !isTeamResolvable(teamsParam, teams) &&
      teams?.length > 0
    ) {
      // data present but not in list, select the first
      mf_Teams(teams[0]?.id || 0);
      mf_TeamDetails(teams[0] || 0);

      const instantRV = {
        leagues: mf_Leagues(),
        players: mf_Players(),
        seasons: mf_Seasons(),
        teams: teams[0]?.id || 0,
        games: mf_Games(),
        drives: pf_Drive(),
        plays: pf_TeamPlay(),
      };

      // replace: true so the unusable team URL doesn't linger in history. It is
      // now reachable by ordinary navigation (leaving Overview with a T1-only
      // team selected), and a pushed entry would make Back land on that URL,
      // re-run this recovery and push forward again - trapping the user.
      // Matches the games recovery below for the same reason.
      navigate(
        `${replaceTokens(window.location.pathname, pageInfo, instantRV)}${
          window.location.search
        }`,
        { replace: true },
      );
    } else {
      mf_TeamDetails(teams?.find((t) => t?.id === mf_Teams()));
    }

    if (players && isFalsyParam(playersParam)) {
      // data present but not in list, select the first
      mf_Players(players[0]?.id || 0);
      mf_PlayerDetails(players[0] || 0);

      const instantRV = {
        leagues: mf_Leagues(),
        players: players[0]?.id || 0,
        seasons: mf_Seasons(),
        teams: mf_Teams(),
        games: mf_Games(),
        drives: pf_Drive(),
        plays: pf_TeamPlay(),
      };

      navigate(
        `${replaceTokens(window.location.pathname, pageInfo, instantRV)}${
          window.location.search
        }`,
        { replace: false },
      );
    } else {
      mf_PlayerDetails(players?.find((p) => p?.id === mf_Players()));
    }

    // add params to URL if empty
    if (currentLocation.length < 1) {
      const instantRV = {
        leagues: mf_Leagues(),
        players: mf_Players(),
        seasons: mf_Seasons(),
        teams: mf_Teams(),
        games: mf_Games(),
        drives: pf_Drive(),
        plays: pf_TeamPlay(),
      };

      navigate(
        `${replaceTokens(window.location.pathname, pageInfo, instantRV)}${
          window.location.search
        }`,
        { replace: false },
      );
    }

    if (gamesData) {
      // Also recover when the URL names a game the loaded list doesn't contain.
      // GET_GAMES is route-scoped - only Game Overview asks for T1 games - so a
      // T1 game selected there is absent from the list on every other route.
      // Without this, the else branch resolves mf_GameDetails to undefined while
      // mf_Games and the URL still hold the missing id, leaving the header
      // dropdown showing an unrelated game and the page with no data.
      if (!isGameResolvable(gamesParam, gamesData)) {
        const firstGame = gamesData?.games?.edges[0]?.node;
        mf_Games(firstGame?.id);
        mf_GameDetails(
          gamesData?.games?.edges.find((game) => game.node.id === firstGame?.id)
            ?.node,
        );

        // Sync the resolved game back into the URL. handleChange navigates to
        // `.../null` the instant the team changes (before this team's games
        // have loaded); without this, the URL stays stuck on `null` even
        // though mf_Games has recovered. Mirrors the teams[0]/players[0]
        // recovery blocks above, which each re-navigate once their default
        // resolves. replace: true so the transient `/null` entry doesn't
        // linger in history.
        //
        // Only do this on routes that actually have a games segment. On pages
        // without one (e.g. player pages) `gamesParam` is undefined and this
        // block still runs (games is falsy), but re-navigating there is a
        // spurious redirect that can destabilise the page - so guard on it.
        if (firstGame?.id && pageInfo && gamesParam !== undefined) {
          const instantRV = {
            leagues: mf_Leagues(),
            players: mf_Players(),
            seasons: mf_Seasons(),
            teams: mf_Teams(),
            games: firstGame.id,
            drives: pf_Drive(),
            plays: pf_TeamPlay(),
          };

          navigate(
            `${replaceTokens(window.location.pathname, pageInfo, instantRV)}${
              window.location.search
            }`,
            { replace: true },
          );
        }
      } else {
        mf_GameDetails(
          gamesData?.games?.edges.find((game) => game.node.id === mf_Games())
            ?.node,
        );
      }
    }
    mf_AllSeasons(seasons);
  }

  // TODO: remove this useEffect once we removed all style references to .dark class
  useEffect(() => {
    document.body.className = ui_isDarkRV ? 'dark' : '';
  }, []);

  return (
    <StyledLayout $withFilters={useFilter}>
      <Header
        pageInfo={pageInfo}
        navigationData={seasonLeagueTeamPlayersData?.navigationData}
        isLoadingNavigationData={isLoading || isLoadingGamesData}
        handleChange={handleChange}
        gamesData={gamesData}
      />
      <div className="core">
        {useFilter && <ContextFilters />}
        <SideNav />
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <Main>
            <Outlet context={[pageInfo, setPageInfo]} />
          </Main>
        </ErrorBoundary>
        <Toast
          isOpen={showToast}
          onClose={() => setToastVisibility(false)}
          onTimerEnd={() => setToastVisibility(false)}
          timer={3000}
          title={toast?.title}
          type={toast?.type || 'success'}
        >
          {toast?.message}
        </Toast>
      </div>
      <WhatsNew />
    </StyledLayout>
  );
};

export default Layout;
